'use strict';

define('vb/private/stateManagement/appPackage',[
  'vb/private/stateManagement/packageFlow',
  'vb/private/stateManagement/context/appPackageContext',
  'vb/private/stateManagement/appPackageExtension',
  'vb/private/services/services',
  'vb/private/utils',
  'vb/errors/httpError',
  'vb/private/constants',
], (PackageFlow, AppPackageContext, AppPackageExtension, Services, Utils, HttpError, Constants) => {
  /**
   * AppPackage class
   */
  class AppPackage extends PackageFlow {
    /**
     * AppPackage constructor
     *
     * @param  {String}     id     a string id to identify the appPackage
     * @param  {Page}       page   the page that contain this appPackage
     * @return {AppPackage}        appPackage object
     */
    constructor(extension, id, page, className = 'AppPackage') {
      super(id, page, `app-packages/${id}/`, className);

      // Make extensionId readonly properties for safety
      Object.defineProperties(this, {
        extensionId: {
          value: extension.id,
          enumerable: true,
        },
        needsExtension: {
          value: !extension.isOptimized,
          enumerable: true,
        },
        package: {
          value: this,
          enumerable: true,
        },
        // The name is always app-pack, app-pack.json, app-pack.js
        name: {
          value: 'app-pack',
          enumerable: true,
        },
      });

      // app-package always come from extension, so override the baseUrl
      this.baseUrl = `${Constants.EXTENSION_PATH}${this.extensionId}/${Constants.ExtensionFolders.SELF}/`;

      // Override the absoluteUrl value defined in the Flow class.
      // this is needed for setting the path builtins variable used to access images resource that are
      // not bundled with the extension.
      this.absoluteUrl = `${extension.ui.baseUrl}/${Constants.ExtensionFolders.SELF}/`;

      this.extensionClass = AppPackageExtension;
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor
     *
     * @return {String} the descriptor loader function name
     */
    // eslint-disable-next-line class-methods-use-this
    get descriptorLoaderName() {
      return 'getAppPackageDescriptor';
    }

    /**
     * The name of the runtime environment function to be used to load the module functions
     *
     * @return {String} the module loader function name
     */
    // eslint-disable-next-line class-methods-use-this
    get functionsLoaderName() {
      return 'getAppPackageFunctions';
    }

    /**
     * returns the LayoutContext constructor used to create the '$' expression context
     * @return { LayoutContext.constructor }
     * @override
     */
    static get ContextType() {
      return AppPackageContext;
    }

    createServices() {
      return Promise.resolve()
        .then(() => {
          const serviceFileMap = this.definition.services || {};
          const options = {
            namespace: this.extensionId,
            relativePath: this.requirePath,
            serviceFileMap,
            expressionContext: this.getAvailableContexts(),
            protocolRegistry: this.application.protocolRegistry,
          };

          this.services = new Services(options);
        });
    }

    /**
     * @returns {string}
     */
    get fullName() {
      return this.name;
    }

    // eslint-disable-next-line class-methods-use-this
    isNavigable() {
      // App-package are always navigable
      return true;
    }

    /**
     * Generate a unique name to be used for the scope.
     * The name is unique and has the name of the class and the path to make it easier to
     * find the scope owner when debugging.
     *
     * @return {String} a new scope name
     */
    getNewScopeName() {
      return `${this.className}/${this.id}`;
    }

    initDefault(definition) {
      const def = definition;

      return super.initDefault(def);
    }

    getScopeResolverMap() {
      return {
        [Constants.APP_PACKAGE_PREFIX]: this,
        [Constants.APPLICATION_PREFIX]: this.application,
      };
    }

    /**
     * Check that a file exist in the this app-package
     * Use the files list provided by he extension
     * Throws a HttpError if the file does not exist
     *
     * @param  {String} path
     */
    checkFileExist(path) {
      const fullPath = `${Constants.ExtensionFolders.SELF}/${path}`;

      // app-package are using extension with a manifest containing a list of files,
      // so we can check if the file exist before doing a fetch that may fail.
      if (!this.application.fileExistsInExtension(this.extensionId, fullPath)) {
        throw new HttpError(404, null, `${fullPath} not found.`);
      }
    }
  }

  return AppPackage;
});

